// TUT-450 — as peças: lista, lista entre processos e transições.
import { demoApi } from "../demo-data";
import type {
  AssignableMembersResponse,
  LegalBrief,
  LegalBriefAcrossProcessesQuery,
  LegalBriefAcrossProcessesResponse,
  LegalBriefCaseOutcomeKey,
  LegalBriefListResponse,
  LegalBriefStatusName,
  LegalBriefTypeKey,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDasPecas = {
  /**
   * TUT-498 (revisão 1, I1) — quem pode elaborar peça (membro ativo que
   * escreve peças), pela chave de peças: o perito chega a ela.
   */
  legalBriefAssignableMembers(): Promise<AssignableMembersResponse> {
    if (useDemoApi) return demoApi.legalBriefAssignableMembers();
    return request<AssignableMembersResponse>("/legal-briefs/assignable-members");
  },
  // Peças processuais (TUT-039). A peça pende do PROCESSO, e não da perícia:
  // o legado chaveia tudo por número de processo, e quesitos são formulados
  // antes de existir perícia.
  legalBriefs(processId: string): Promise<LegalBriefListResponse> {
    if (useDemoApi) return demoApi.legalBriefs(processId);
    return request<LegalBriefListResponse>(
      `/processes/${encodeURIComponent(processId)}/legal-briefs`,
    );
  },
  legalBriefsAcrossProcesses(
    query: LegalBriefAcrossProcessesQuery = {},
  ): Promise<LegalBriefAcrossProcessesResponse> {
    if (useDemoApi) return demoApi.legalBriefsAcrossProcesses(query);
    const params = new URLSearchParams({
      page: String(query.page ?? 1),
      pageSize: String(query.pageSize ?? 20),
    });
    if (query.search) params.set("search", query.search);
    for (const type of query.types ?? []) params.append("type", type);
    for (const status of query.statuses ?? []) params.append("status", status);
    if (query.mine) params.set("mine", "true");
    // TUT-249 — os dois filtros do desenho da cliente, aplicados no SERVIDOR.
    if (query.assignedUserId)
      params.set("assignedUserId", query.assignedUserId);
    if (query.clientOrganizationId)
      params.set("clientOrganizationId", query.clientOrganizationId);
    if (query.defendantOrganizationId)
      params.set("defendantOrganizationId", query.defendantOrganizationId);
    if (query.dueFrom) params.set("dueFrom", query.dueFrom);
    if (query.dueTo) params.set("dueTo", query.dueTo);
    if (query.sort) params.set("sort", query.sort);
    if (query.dir) params.set("dir", query.dir);
    return request<LegalBriefAcrossProcessesResponse>(`/legal-briefs?${params}`);
  },
  openLegalBrief(
    processId: string,
    input: { type: LegalBriefTypeKey; ordinal?: number; subject?: string },
  ): Promise<LegalBrief> {
    if (useDemoApi) return demoApi.openLegalBrief(processId, input);
    return request<LegalBrief>(
      `/processes/${encodeURIComponent(processId)}/legal-briefs`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  transitionLegalBrief(
    briefId: string,
    input: {
      to: LegalBriefStatusName;
      reason?: string | null;
      reviewOutcome?: "REVIEWED" | "NOT_APPLICABLE";
      caseOutcome?: LegalBriefCaseOutcomeKey;
    },
  ): Promise<LegalBrief> {
    if (useDemoApi) return demoApi.transitionLegalBrief(briefId, input);
    return request<LegalBrief>(
      `/legal-briefs/${encodeURIComponent(briefId)}/transitions`,
      {
        method: "POST",
        body: JSON.stringify({
          to: input.to,
          ...(input.reason ? { reason: input.reason } : {}),
          ...(input.reviewOutcome
            ? { reviewOutcome: input.reviewOutcome }
            : {}),
          ...(input.caseOutcome ? { caseOutcome: input.caseOutcome } : {}),
        }),
      },
    );
  },
  /** TUT-333 — desfaz o último movimento da peça (quem o fez, sem entrega). */
  undoLegalBriefTransition(briefId: string): Promise<LegalBrief> {
    if (useDemoApi) return demoApi.undoLegalBriefTransition(briefId);
    return request<LegalBrief>(
      `/legal-briefs/${encodeURIComponent(briefId)}/transitions/undo`,
      { method: "POST" },
    );
  },
  /**
   * TUT-524 (D18) — "eu pego": a peça passa a quem pede, se ninguém pegou
   * antes. Quem chega depois recebe a recusa "Já está com {nome}".
   */
  claimLegalBrief(briefId: string): Promise<LegalBrief> {
    if (useDemoApi) return demoApi.claimLegalBrief(briefId);
    return request<LegalBrief>(`/legal-briefs/${encodeURIComponent(briefId)}/claim`, { method: "POST" });
  },
  reopenLegalBrief(
    briefId: string,
    input: { reason: string },
  ): Promise<LegalBrief> {
    if (useDemoApi) return demoApi.reopenLegalBrief(briefId, input);
    return request<LegalBrief>(
      `/legal-briefs/${encodeURIComponent(briefId)}/reopen`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
};
